/* eslint-disable react/jsx-curly-newline */
import React, { forwardRef, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from './Header.styles'
import Dropdown from 'react-bootstrap/Dropdown'
import AuthContext from '@contexts/Auth'
import PanelContext from '@contexts/Panel'
import '@components/Header/header.css'
import { CiLogout, CiUser } from 'react-icons/ci'
import { LuHeart, LuShoppingCart, LuPackage, LuHelpCircle, LuMessageCircle } from 'react-icons/lu'
import { HiOutlineMenu } from 'react-icons/hi'
import Logotipo from '@resources/imgs/logo-proseed.webp'
import { displayOnProduction } from '@helpers/Utils'
import NotificationBell from '@components/NotificationBell/NotificationBell'

const Item = forwardRef(({ children, onClick, }: any, ref: any) => (
  <Button
    ref={ref}
    onClick={e => {
      e.preventDefault()
      onClick(e)
    }}
  >{children}</Button>
))

Item.displayName = 'Item'

const LinkItem = forwardRef(({ children, }: any, ref: any) => (
  <Button
    ref={ref}
  >{children}</Button>
))

LinkItem.displayName = 'Item'

const UserItem = forwardRef(({ children, onClick }: any, ref: any) => (
  <Button
    ref={ref}
    onClick={(e) => {
      e.preventDefault()
      onClick(e)
    }}
  >{children}</Button>
))

UserItem.displayName = 'UserItem'

type HeaderProps = {
  showSidebarOpenButton?: boolean
}

const Header: React.FC<HeaderProps> = ({ showSidebarOpenButton = false }) => {
  const { user, cartCount, favoritesCount, inProgressOrders, isLoading, logout } = useContext(AuthContext)
  const { setMenuOpen } = useContext(PanelContext)

  const navigate = useNavigate()

  if (!isLoading && !user) {
    navigate('/')
  }

  const isPatient = user?.roles?.some((role: any) => role.name === 'Paciente')
  const initials = user?.name?.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase() ?? ''
  const roleName = user?.roles?.[0]?.name ?? ''

  const headerContent = (
    <div>
      <div className="row m-0 align-items-center justify-content-between">
        <div className="col-6 col-sm-6 col-md-6 col-xl-0">
          <Link className="link" to="/home">
            <img src={Logotipo} alt="" className="img-fluid d-xl-none" />
          </Link>
        </div>
        <div className="col-6 col-sm-6 col-md-6 col-xl-12">
          <div>
            <nav className="navbar navbar-expand-lg main-navbar d-flex justify-content-end pr-0 navbar-header">
              <ul className="navbar-nav navbar-right d-flex align-items-center" style={{ gap: 4 }}>
                {displayOnProduction() && <NotificationBell />}

                <li className="dropdown dropdown-list-toggle">
                  <Dropdown className="beep d-flex">
                    <Dropdown.Toggle as={LinkItem} variant="primary">
                      <Link to="/favoritos" style={{ position: 'relative', display: 'flex' }}>
                        <LuHeart size={22} />
                        {favoritesCount > 0 ? (
                          <span className="badge badge-pink favorites-badge">{favoritesCount}</span>
                        ) : null}
                      </Link>
                    </Dropdown.Toggle>
                  </Dropdown>
                </li>

                {displayOnProduction() && isPatient && (
                  <li className="dropdown dropdown-list-toggle mr-0 mr-lg-2">
                    <Dropdown className="beep d-flex">
                      <Dropdown.Toggle as={LinkItem} variant="primary">
                        <Link target="_blank" to="/carrinho" style={{ position: 'relative', display: 'flex' }}>
                          <LuShoppingCart size={22} />
                          {cartCount > 0 ? (
                            <span className="badge badge-pink favorites-badge">{cartCount}</span>
                          ) : null}
                        </Link>
                      </Dropdown.Toggle>
                    </Dropdown>
                  </li>
                )}

                {showSidebarOpenButton && (
                  <li className="dropdown d-xl-none ml-2">
                    <button
                      className="btn btn-link p-0"
                      onClick={() => setMenuOpen(true)}
                      style={{ color: '#6c757d' }}
                    >
                      <HiOutlineMenu size={26} />
                    </button>
                  </li>
                )}

                <li aria-hidden="true" className="d-none d-lg-block" style={{ width: 1, height: 28, backgroundColor: '#E5E7EB', margin: '0 4px', alignSelf: 'center', listStyle: 'none', flexShrink: 0 }} />

                <li className={`dropdown ${showSidebarOpenButton ? 'd-none d-lg-block' : ''}`}>
                  <Dropdown>
                    <Dropdown.Toggle as={UserItem} variant="primary">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{ width: 40, height: 40, backgroundColor: '#FFE4EE', color: '#FA5A84', fontWeight: 700, fontSize: '0.875rem' }}
                      >
                        {initials}
                      </div>
                      <div className="d-none d-md-block ml-2 text-left">
                        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#202020', lineHeight: 1.2 }}>{user?.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>{roleName}</div>
                      </div>
                    </Dropdown.Toggle>

                    <Dropdown.Menu renderOnMount className="user-dropdown-menu" style={{ padding: '8px 0', minWidth: 200, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
                      <Link to="/perfil" target="_blank" className="dropdown-item">
                        <CiUser size={18} /> Perfil
                      </Link>

                      {displayOnProduction() && inProgressOrders.length > 0 && (
                        <Link to="/pedidos" target="_blank" className="dropdown-item">
                          <LuPackage size={18} /> Pedidos
                        </Link>
                      )}

                      <Link to="/faq" target="_blank" className="dropdown-item">
                        <LuHelpCircle size={18} /> FAQ
                      </Link>

                      <Link to="/contato" target="_blank" className="dropdown-item">
                        <LuMessageCircle size={18} /> Contato
                      </Link>

                      <div style={{ height: 1, backgroundColor: '#e9ecef', margin: '6px 12px' }} />

                      <a href="#" className="dropdown-item dropdown-item-danger" onClick={logout}>
                        <CiLogout size={18} /> Sair
                      </a>
                    </Dropdown.Menu>
                  </Dropdown>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  )

  if (!showSidebarOpenButton) return headerContent

  return (
    <div className="header-full-width">
      {headerContent}
    </div>
  )
}

export default Header
